import { useState, useCallback } from 'react';
import { fetchTeSession, fetchTeTransfers, saveTeSession } from '../data/te';
import { getPending } from '../data/local/pending';

export const useTESession = (laboratoryId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const loadTEData = useCallback(async (serviceId) => {
    if (!serviceId || !laboratoryId) return null;
    setLoading(true);
    setError(null);
    try {
      const pendente = await getPending('te', serviceId);
      if (pendente) return pendente;

      const session = await fetchTeSession(serviceId);
      if (!session) return null;

      const transfers = await fetchTeTransfers(session.id);

      return {
        _session_id: session.id,
        session: {
          date:            session.session_date,
          farm:            session.farm,
          city:            session.city,
          technician_name: session.technician_name,
          aspiration_date: session.aspiration_date
        },
        transfers: (transfers || []).map(t => ({
          _db_id:         t.id,
          id:             t.id,
          embryo_id:      t.embryo_id,
          embryo_number:  t.embryo_number,
          embryo_source:  t.embryo_source,
          donor_name:     t.donor_name,
          bull:           t.bull_name,
          recipient: {
            id:         null,
            animal_id:  t.recipient_id,
            identifier: t.recipient_identifier,
            breed:      t.recipient_breed
          },
          pre_evaluation: {
            corpus_luteum: t.corpus_luteum,
            cl_quality:    t.cl_quality,
            uterine_tone:  t.uterine_tone
          },
          status: t.status,
          procedure: {
            time:          t.procedure_time,
            horn:          t.horn,
            difficulty:    t.difficulty,
            complications: t.complications,
            confirmation:  t.confirmation
          },
          dates: {
            expected_dg30:  t.expected_dg30,
            expected_dg60:  t.expected_dg60,
            expected_birth: t.expected_birth
          },
          observations: t.observations
        })),
        completed:    session.completed,
        completed_at: session.completed_at
      };
    } catch (err) {
      console.error('useTESession.loadTEData:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  /**
   * Salva sessão TE via RPC — uma única chamada ao banco.
   * Re-linking de embryo_id feito no SQL pelo service_number.
   */
  const saveTEData = useCallback(async (serviceId, teData) => {
    if (!serviceId || !laboratoryId || !teData) return null;
    setLoading(true);
    setError(null);
    try {
      const { session, transfers = [], completed, completed_at } = teData;

      return await saveTeSession({
        p_service_id:    serviceId,
        p_laboratory_id: laboratoryId,
        p_session: {
          date:            session?.date            || null,
          farm:            session?.farm            || null,
          city:            session?.city            || null,
          technician_name: session?.technician_name || null,
          aspiration_date: session?.aspiration_date || null,
          completed:       completed || false,
          completed_at:    completed ? (completed_at || new Date().toISOString()) : null,
        },
        p_transfers: transfers.map((t, idx) => ({
          // O id lido do banco volta para ele: a função atualiza a linha em
          // vez de recriá-la, e a DG que aponta para ela continua valendo.
          id:                   t._db_id               || null,
          sort_order:           idx,
          embryo_id:            t.embryo_id            || null,
          embryo_number:        t.embryo_number        || null,
          embryo_source:        t.embryo_source        || null,
          donor_name:           t.donor_name           || null,
          bull_name:            t.bull || t.bull_name  || null,
          recipient_animal_id:  t.recipient?.animal_id || null,
          recipient_identifier: t.recipient?.identifier || t.recipient_identifier || null,
          recipient_breed:      t.recipient?.breed     || t.recipient_breed || null,
          corpus_luteum:        t.pre_evaluation?.corpus_luteum || false,
          cl_quality:           t.pre_evaluation?.cl_quality    || null,
          uterine_tone:         t.pre_evaluation?.uterine_tone  || null,
          status:               t.status || null,
          procedure_time:       t.procedure?.time          || null,
          horn:                 t.procedure?.horn          || null,
          difficulty:           t.procedure?.difficulty    || null,
          complications:        t.procedure?.complications || 'Nenhuma',
          confirmation:         t.procedure?.confirmation  || null,
          expected_dg30:        t.dates?.expected_dg30     || null,
          expected_dg60:        t.dates?.expected_dg60     || null,
          expected_birth:       t.dates?.expected_birth    || null,
          observations:         t.observations             || null,
        })),
      }, teData);
    } catch (err) {
      console.error('useTESession.saveTEData:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  return { loading, error, loadTEData, saveTEData };
};
